import {
  BudgetSheetLink,
  BudgetSheetPayload,
  CreateProduct,
  PmProductsParams,
  ProductManagerCandidate,
  UpdateProduct,
  RndDossier,
} from '@data/utils/types';
import {
  useInfiniteQuery,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query';

import type {
  AikidoRepository,
  GithubRepositoryListing,
  SentryAccess,
  SentryProject,
  IError,
  PmProductsInfiniteData,
  PmParams,
  SprintEntity,
  ProductParams,
  ProductEntity,
  ListResponse,
  ProductCrashFree,
  ProductDependencies,
  ProductQualityAudit,
  ProductProductivityReport,
  AuditReportKind,
  ProductAuditReport,
  ProductResponseTime,
  ProductSecurity,
} from '@data';
import { useSprintPages } from './useSprintPages';
import * as api from '../api';
import * as queryKeys from '../constants/keys';
import { invalidatePmProductLists } from './pm-product-lists';

// No caller passes a span yet: the version picker that scopes the read to a term is slice 5 of epic 16.
export const useProduct = (productId?: string): UseQueryResult<ProductEntity, IError> => {
  const queryClient = useQueryClient();
  const enabled = !!productId;
  return useQuery({
    queryKey: [queryKeys.PRODUCT, productId],
    queryFn: () => api.fetchProduct(productId!),
    enabled,
    staleTime: 1000,
    placeholderData: () => {
      const lists = queryClient.getQueriesData<ListResponse<ProductEntity>>({
        queryKey: [queryKeys.PRODUCTS],
        exact: false,
      });
      return lists.flatMap(([, data]) => data?.items ?? []).find(product => product.id === productId);
    },
  });
};

export const useListProducts = (
  organizationId: string | undefined,
  params?: ProductParams,
): UseQueryResult<{ items: ProductEntity[]; count: number }, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCTS, organizationId, params],
    queryFn: () => api.fetchProducts(organizationId!, params),
    enabled: !!organizationId,
    staleTime: 1000,
  });

export const useListProductManagers = (
  productId?: string,
): UseQueryResult<ListResponse<ProductManagerCandidate>, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_MANAGERS, productId],
    queryFn: () => api.fetchProductManagers(productId!),
    enabled: !!productId,
  });

export const useListPmProducts = (
  organizationId: string | undefined,
  params: PmProductsParams,
  // Held back while a caller is still working out its params — a window derived from the sprint list
  // is not known on the first render, and asking without one answers a different question.
  enabled = true,
): UseQueryResult<ListResponse<ProductEntity>, IError> =>
  useQuery({
    queryKey: [queryKeys.PM_PRODUCTS, { organizationId, params }],
    queryFn: () => api.fetchPmProducts(organizationId!, params),
    enabled: enabled && !!organizationId,
  });

export const useInfiniteListPmProducts = (
  organizationId: string | undefined,
  params: PmParams,
  enabled: boolean,
  sprints?: SprintEntity[],
) =>
  useInfiniteQuery<PmProductsInfiniteData, IError>({
    queryKey: [queryKeys.PM_PRODUCTS, { organizationId, params }],
    queryFn: ({ pageParam }) => api.fetchInfinitePmProducts(organizationId!, params, { pageParam }),
    initialPageParam: undefined as
      | {
          from?: Date | string;
          to?: Date | string;
          sprintNumber?: number;
          sprintId?: string;
        }
      | undefined,
    getNextPageParam: (lastPage: {
      from: Date | string;
      to: Date | string;
      sprintNumber: number;
      sprintId: string;
    }) => {
      const lastPageFromDate = new Date(lastPage.from);
      const sortedSprints = (sprints ?? []).sort(
        (a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime(),
      );
      const nearestSprintIndex = sortedSprints.findIndex(sprint => new Date(sprint.endDate) >= lastPageFromDate);
      const sprint = nearestSprintIndex > 0 ? sortedSprints[nearestSprintIndex - 1] : undefined;
      if (!sprint) return undefined;

      return {
        from: sprint?.startDate,
        to: sprint?.endDate,
        sprintNumber: sprint?.number,
        sprintId: sprint?.id,
      };
    },
    placeholderData: keepPreviousData,
    enabled: enabled && !!organizationId,
  });

// The three pickers on a product's page. Each is its own query, because each tool is reachable or not on
// its own: Sentry being unreachable must not empty the Aikido picker. The handlers answer an empty list
// rather than an error when a tool has no credentials, so an unconfigured environment shows a picker
// saying nothing is available instead of a broken section.
export const useSentryProjects = (productId?: string): UseQueryResult<ListResponse<SentryProject>, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_SENTRY_PROJECTS, productId],
    queryFn: () => api.fetchSentryProjects(productId!),
    enabled: !!productId,
  });

export const useAikidoRepositories = (productId?: string): UseQueryResult<ListResponse<AikidoRepository>, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_AIKIDO_REPOSITORIES, productId],
    queryFn: () => api.fetchAikidoRepositories(productId!),
    enabled: !!productId,
  });

export const useGithubRepositories = (productId?: string): UseQueryResult<GithubRepositoryListing, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_GITHUB_REPOSITORIES, productId],
    queryFn: () => api.fetchGithubRepositories(productId!),
    enabled: !!productId,
  });

// Adding a product from the Manage list. Every products list is invalidated rather than optimistically
// patched: the row the API answers with carries the managers, the client and the counts the columns read,
// none of which the dialog knows.
export const useCreateProduct = (
  organizationId: string,
): UseMutationResult<ProductEntity, IError, CreateProduct, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: CreateProduct) => api.createProduct(organizationId, values),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_PRODUCTS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCTS] });
      // A product created with a client changes what the clients screen lists against them.
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENTS] });
    },
  });
};

// Removing a product outright.
export const useDeleteProduct = (): UseMutationResult<void, IError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) => api.deleteProduct(productId),
    onSettled: (_data, _error, productId) => {
      // Marked stale rather than removed, and deliberately not refetched: the page that asked for this
      // product is still mounted for the moment it takes to leave it, and dropping its data under it sends
      // it straight back for a product that no longer exists — a 403 in the log and an error on screen.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCT, productId], refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_PRODUCTS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCTS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENTS] });
    },
  });
};

export const useUpdateProduct = (
  organizationId: string,
  params: PmProductsParams,
): UseMutationResult<ProductEntity, IError, { productId: string; values: UpdateProduct }, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, values }) => api.updateProduct(productId, values),
    onMutate: async ({ productId, values }) => {
      const previousPmProducts = queryClient.getQueryData<ListResponse<ProductEntity>>([
        queryKeys.PM_PRODUCTS,
        { organizationId, params },
      ]);

      if (previousPmProducts) {
        queryClient.setQueryData<ListResponse<ProductEntity>>([queryKeys.PM_PRODUCTS, { organizationId, params }], {
          ...previousPmProducts,
          items: previousPmProducts?.items?.map(product => {
            if (product.id === productId) {
              // A cleared field goes over the wire as `null` where the row holds it as absent. This row
              // is display-only until the refetch replaces it, so the shapes differing costs nothing.
              return { ...product, ...values } as ProductEntity;
            }
            return product;
          }),
        });
      }

      return { previousPmProducts };
    },

    onError: (_, __, context: { previousPmProducts: ListResponse<ProductEntity> }) => {
      queryClient.setQueryData([queryKeys.PM_PRODUCTS, { organizationId, params }], context.previousPmProducts);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_PRODUCTS, { organizationId, params }] });
      // Editing a product changes what the products reporting list shows, and that list now reads a cache
      // entry per sprint as well as the plain PM list.
      invalidatePmProductLists(queryClient);
    },
  });
};

export const useUpdateProductManagers = (): UseMutationResult<
  ProductEntity,
  IError,
  { productId: string; membershipIds: string[] },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, membershipIds }) => api.updateProductManagers(productId, membershipIds),
    onSettled: (_data, _error, { productId }) => {
      // The whole prefix: a params-keyed invalidation leaves every other list showing the
      // previous managers.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_PRODUCTS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCT_MANAGERS, productId] });
    },
  });
};

/** The dashboard's health cards read what the product is linked to, so a new link must not wait out their stale time. */
const invalidateProductHealth = (queryClient: QueryClient, productId: string): void => {
  [
    queryKeys.PRODUCT_RESPONSE_TIME,
    queryKeys.PRODUCT_CRASH_FREE,
    queryKeys.PRODUCT_DEPENDENCIES,
    queryKeys.PRODUCT_SECURITY,
  ].forEach(key => queryClient.invalidateQueries({ queryKey: [key, productId] }));
};

// The product page's own save. Deliberately not `useUpdateProduct`: that one optimistically rewrites the
// products-list cache, which a page reached by URL never has, and a list-shaped hook on a detail page is
// how a mutation ends up aborted before the request goes out. This one just invalidates what it changed.
export const useUpdateProductDetail = (
  productId: string,
): UseMutationResult<ProductEntity, IError, UpdateProduct, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: UpdateProduct) => api.updateProduct(productId, values),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCT, productId] });
      // The whole prefix: the managers and client columns on every products list read what this wrote.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_PRODUCTS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCTS] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENTS] });
      invalidateProductHealth(queryClient, productId);
    },
  });
};

/**
 * Links a personal Sentry token to this product, for one in a client's own Sentry organization.
 *
 * Invalidates the projects list as well as the product: the whole point of the token is that the picker
 * has different options through it, and the dialog closes straight back onto that picker.
 */
export const useLinkProductSentryToken = (
  productId: string,
): UseMutationResult<SentryAccess, IError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => api.linkProductSentryToken(productId, token),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCT, productId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCT_SENTRY_PROJECTS, productId] });
      invalidateProductHealth(queryClient, productId);
    },
  });
};

export const useUnlinkProductSentryToken = (productId: string): UseMutationResult<void, IError, void, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.unlinkProductSentryToken(productId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCT, productId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PRODUCT_SENTRY_PROJECTS, productId] });
      invalidateProductHealth(queryClient, productId);
    },
  });
};

/*
 * The organization's own pickers, for a product that does not exist yet. Same lists, same shapes; the
 * product-scoped hooks above stay as they are for a product's own page.
 */
export const useOrganizationSentryProjects = (
  organizationId?: string,
): UseQueryResult<ListResponse<SentryProject>, IError> =>
  useQuery({
    queryKey: [queryKeys.ORGANIZATION_SENTRY_PROJECTS, organizationId],
    queryFn: () => api.fetchOrganizationSentryProjects(organizationId!),
    enabled: !!organizationId,
  });

export const useOrganizationAikidoRepositories = (
  organizationId?: string,
): UseQueryResult<ListResponse<AikidoRepository>, IError> =>
  useQuery({
    queryKey: [queryKeys.ORGANIZATION_AIKIDO_REPOSITORIES, organizationId],
    queryFn: () => api.fetchOrganizationAikidoRepositories(organizationId!),
    enabled: !!organizationId,
  });

export const useOrganizationGithubRepositories = (
  organizationId?: string,
): UseQueryResult<GithubRepositoryListing, IError> =>
  useQuery({
    queryKey: [queryKeys.ORGANIZATION_GITHUB_REPOSITORIES, organizationId],
    queryFn: () => api.fetchOrganizationGithubRepositories(organizationId!),
    enabled: !!organizationId,
  });

/**
 * The five health readings, one hook each.
 *
 * They share their caching for one reason: every one of them is a third-party call behind our API, all
 * five open at once, and the dashboard's contract is that a slow or dead source costs its own card and
 * nothing else. Five minutes because Sentry aggregates over 90 days and Aikido scans on its own
 * schedule — a fresh read per minute would tell you the same number.
 *
 * `retry: 1` rather than none. Reaching GitHub, Aikido or Sentry fails now and then, and without the one
 * retry a single blip is cached as "nothing known" for the whole stale time — which is exactly the
 * unknown state a client would read as a verdict.
 */
const healthQuery = { staleTime: 5 * 60 * 1000, retry: 1 } as const;

export const useProductResponseTime = (productId?: string): UseQueryResult<ProductResponseTime, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_RESPONSE_TIME, productId],
    queryFn: () => api.fetchProductResponseTime(productId!),
    enabled: !!productId,
    ...healthQuery,
  });

export const useProductCrashFree = (productId?: string): UseQueryResult<ProductCrashFree, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_CRASH_FREE, productId],
    queryFn: () => api.fetchProductCrashFree(productId!),
    enabled: !!productId,
    ...healthQuery,
  });

export const useProductDependencies = (productId?: string): UseQueryResult<ProductDependencies, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_DEPENDENCIES, productId],
    queryFn: () => api.fetchProductDependencies(productId!),
    enabled: !!productId,
    ...healthQuery,
  });

export const useProductSecurity = (productId?: string): UseQueryResult<ProductSecurity, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_SECURITY, productId],
    queryFn: () => api.fetchProductSecurity(productId!),
    enabled: !!productId,
    ...healthQuery,
  });

/**
 * The quarters this product has been audited in, most recent first. Bodies are left out, so this stays
 * cheap enough to decide whether the Quality tab appears at all.
 */
export const useProductQualityAudits = (
  productId?: string,
): UseQueryResult<ListResponse<ProductQualityAudit>, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_QUALITY_AUDITS, productId],
    queryFn: () => api.fetchProductQualityAudits(productId!),
    enabled: !!productId,
    // An audit lands once a quarter, so this is as static as anything the app reads.
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

/** One quarter's audit in full, fetched only once the tab knows which quarter it is showing, compared with `compareTo` when given. */
export const useProductQualityAudit = (
  productId?: string,
  year?: number,
  quarter?: number,
  compareTo?: string,
): UseQueryResult<ProductQualityAudit, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_QUALITY_AUDIT, productId, year, quarter, compareTo ?? null],
    queryFn: () => api.fetchProductQualityAudit(productId!, year!, quarter!, compareTo),
    enabled: !!productId && !!year && !!quarter,
    // Only the same quarter's uncompared body may stand in; another quarter or another comparison never does.
    placeholderData: (previous, previousQuery) => {
      const [, wasProduct, wasYear, wasQuarter, wasCompareTo] = previousQuery?.queryKey ?? [];
      const sameQuarter = wasProduct === productId && wasYear === year && wasQuarter === quarter;
      return sameQuarter && wasCompareTo === null ? previous : undefined;
    },
    staleTime: 60 * 60 * 1000,
    // A compared quarter that is not there will not appear on a second try, so its notice shows at once.
    retry: (failures, error) =>
      !(compareTo && (error as { response?: { status?: number } })?.response?.status === 404) && failures < 1,
  });

/** The quarters this product has a productivity report for, most recent first, bodies left out. */
export const useProductProductivityReports = (
  productId?: string,
): UseQueryResult<ListResponse<ProductProductivityReport>, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_PRODUCTIVITY_REPORTS, productId],
    queryFn: () => api.fetchProductProductivityReports(productId!),
    enabled: !!productId,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

export const useProductProductivityReport = (
  productId?: string,
  year?: number,
  quarter?: number,
): UseQueryResult<ProductProductivityReport, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_PRODUCTIVITY_REPORT, productId, year, quarter],
    queryFn: () => api.fetchProductProductivityReport(productId!, year!, quarter!),
    enabled: !!productId && !!year && !!quarter,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

/** Which productivity and security reports the product's repositories carry; the pages come one at a time. */
export const useProductAuditReports = (productId?: string): UseQueryResult<ListResponse<ProductAuditReport>, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_AUDIT_REPORTS, productId],
    queryFn: () => api.fetchProductAuditReports(productId!),
    enabled: !!productId,
    // A report is committed when an audit is run, which is no more than a few times a year.
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

export const useProductAuditReport = (
  productId?: string,
  kind?: AuditReportKind,
  sha?: string,
): UseQueryResult<ProductAuditReport, IError> =>
  useQuery({
    // Keyed on the sha too, so a newly committed report is read rather than served from the cache.
    queryKey: [queryKeys.PRODUCT_AUDIT_REPORT, productId, kind, sha],
    queryFn: () => api.fetchProductAuditReport(productId!, kind!),
    enabled: !!productId && !!kind && !!sha,
    staleTime: 60 * 60 * 1000,
    retry: 1,
  });

/**
 * The products table for each sprint of the open page, one query per sprint.
 *
 * Each sprint keeps its own cache entry, so a sprint already opened comes back without asking again.
 * See useSprintPages.
 */
export const usePmProductsBySprint = (
  organizationId: string | undefined,
  sprints: SprintEntity[] | undefined,
  params: PmParams,
  enabled: boolean,
) =>
  useSprintPages<ProductEntity>(
    sprints,
    sprint => ({
      queryKey: [queryKeys.PM_PRODUCTS_BY_SPRINT, { organizationId, sprintId: sprint.id, params }],
      queryFn: async () =>
        (
          await api.fetchInfinitePmProducts(organizationId!, params, {
            pageParam: { from: sprint.startDate, to: sprint.endDate, sprintNumber: sprint.number, sprintId: sprint.id },
          })
        ).items,
    }),
    enabled && !!organizationId,
  );

export const useRndDossier = (productId?: string): UseQueryResult<RndDossier, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_RND_DOSSIER, productId],
    queryFn: () => api.fetchRndDossier(productId!),
    enabled: !!productId,
  });

export const useUpdateRndDossier = (): UseMutationResult<
  RndDossier,
  IError,
  { productId: string; values: RndDossier },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, values }) => api.updateRndDossier(productId, values),
    onSuccess: (dossier, { productId }) => {
      queryClient.setQueryData([queryKeys.PRODUCT_RND_DOSSIER, productId], dossier);
    },
  });
};

/** Null rather than an error where there is none: nobody having synced is the ordinary case. */
export const useBudgetSheet = (productId: string, year: number): UseQueryResult<BudgetSheetLink | null, IError> =>
  useQuery({
    queryKey: [queryKeys.PRODUCT_BUDGET_SHEET, productId, year],
    queryFn: () => api.fetchBudgetSheet(productId, year),
    enabled: !!productId && !!year,
    // Asked once. A product with no workbook is the ordinary answer, not something to retry into.
    retry: false,
  });

/** Written into the cache rather than invalidated: the answer is what the header is about to show. */
export const useSyncBudgetSheet = (
  productId: string,
): UseMutationResult<BudgetSheetLink, IError, BudgetSheetPayload, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: BudgetSheetPayload) => api.syncBudgetSheet(productId, payload),
    onSuccess: (link, payload) => {
      queryClient.setQueryData([queryKeys.PRODUCT_BUDGET_SHEET, productId, payload.year], link);
    },
  });
};
